import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Star,
  Lock,
  CircleDot,
  BookOpen,
  Printer,
  ShieldCheck,
  Map,
  Gamepad2
} from 'lucide-react';
import { useLab, studentBase, getCourseTemplateForChild, resolveLabChildId } from '../LabContext';
import { LabLayout, Card, StatusPill, ParentPreviewBadge } from '../LabUI';
import { computeDayStatus, getWeeksForCourse } from '../data/lessons';
import CourseMap from '../components/CourseMap';

const MyPath = () => {
  const { t, tx, user, language, childProgress, refreshBackendStudent } = useLab();
  const params = useParams();
  const location = useLocation();
  const childId = resolveLabChildId({
    routeStudentId: params.studentId,
    pathname: location.pathname,
    user,
  });
  const base = studentBase(childId);
  const progress = childProgress(childId);
  const isParent = user?.role === 'parent' || user?.role === 'super_admin';
  const courseTemplate = getCourseTemplateForChild(user, childId);
  const weeks = getWeeksForCourse(courseTemplate);
  const [openWeek, setOpenWeek] = useState('week-1');

  // Parent preview: hydrate this child's progress from the backend so the
  // path map shows the right green/locked bubbles even though the parent
  // never logged in as the child.
  useEffect(() => {
    if (!isParent || !childId) return;
    refreshBackendStudent(childId).catch(() => { /* ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParent, childId]);

  // Child-aware path title — Anita's path on /lab/student/anita/path,
  // Max's path on /lab/student/max/path, generic for any future child.
  const pathTitleKey =
    childId === 'max' ? 'pathTitle'
    : childId === 'anita' ? 'pathTitleAnita'
    : 'pathTitleGeneric';

  // Empty curriculum (Phase 0: Anita has no lessons yet). Render a calm
  // placeholder instead of accidentally falling back to Max content.
  if (!weeks.length) {
    return (
      <LabLayout desktopWide>
        <div className="lg:max-w-4xl lg:mx-auto">
          <section className="text-center mt-2">
            <h1
              className="text-3xl text-[#f3d9a4]"
              style={{ fontFamily: 'var(--font-heading)' }}
              data-testid="path-title"
            >
              {t(pathTitleKey)}
            </h1>
          </section>
          <Card className="mt-8 p-6 text-center" data-testid="path-empty-course">
            <p className="text-amber-100/85 font-serif">
              {language === 'ru'
                ? 'Курс Аниты сейчас готовится.'
                : 'Anita curriculum is being prepared.'}
            </p>
          </Card>
        </div>
      </LabLayout>
    );
  }

  return (
    <LabLayout desktopWide>
      <CourseMap />
      <div className="lg:max-w-4xl lg:mx-auto">
      <section className="text-center mt-2">
        <h1
          className="text-3xl text-[#f3d9a4]"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="path-title"
        >
          {t(pathTitleKey)}
        </h1>
        <p className="mt-2 text-amber-200/65 font-serif text-sm">
          {t('pathSubtitle')}
        </p>
        <div className="flex justify-center">
          <ParentPreviewBadge />
        </div>
      </section>

      {/* Status legend */}
      <Card className="mt-5 p-4 grid grid-cols-4 gap-2 text-center">
        <Legend kind="open" t={t} />
        <Legend kind="in_progress" t={t} />
        <Legend kind="completed" t={t} />
        <Legend kind="locked" t={t} />
      </Card>

      {/* Weeks */}
      <div className="mt-5 space-y-3">
        {weeks.map((week) => {
          const isOpen = openWeek === week.id;
          // Parents / super-admins can always expand and preview every
          // week, including ones marked locked at the data level.
          const isExpandable = (isParent || !week.locked) && Array.isArray(week.days);
          // Always toggle: an already-open week collapses on the next click,
          // a closed week expands. setOpenWeek(null) closes the only open
          // accordion (we render at most one expanded week at a time).
          const handleToggle = () => {
            if (!isExpandable) return;
            setOpenWeek((prev) => (prev === week.id ? null : week.id));
          };
          return (
            <Card key={week.id} className="overflow-hidden">
              <button
                type="button"
                onClick={handleToggle}
                aria-expanded={isExpandable ? isOpen : undefined}
                aria-controls={isExpandable ? `path-week-${week.id}-panel` : undefined}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left ${
                  isExpandable
                    ? 'cursor-pointer hover:bg-amber-300/5'
                    : 'cursor-default'
                }`}
                data-testid={`path-week-${week.id}-toggle`}
                data-open={isOpen ? 'true' : 'false'}
              >
                <span className="text-[#f5ebe0]/90 font-sans">
                  {t('weekLabel')} {week.id.split('-')[1]} — {tx(week.title)}
                </span>
                <span className="flex items-center gap-2 text-amber-200/55">
                  {/* Lock glyph only when truly inaccessible (non-parent on a
                      data-locked week). For everyone who can expand, render
                      the chevron — flipped when the panel is open — so the
                      affordance to collapse is always visible. */}
                  {!isExpandable && week.locked ? (
                    <Lock className="w-4 h-4" />
                  ) : isExpandable ? (
                    isOpen ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )
                  ) : null}
                </span>
              </button>

              {isExpandable && isOpen && (
                <div className="px-4 pb-4 pt-1" id={`path-week-${week.id}-panel`}>
                  <ol className="relative ml-2 border-l border-amber-300/15 pl-5 space-y-4">
                    {week.days.map((day) => (
                      <DayRow
                        key={day.id}
                        day={day}
                        weekId={week.id}
                        progress={progress}
                        base={base}
                        isParent={isParent}
                        language={language}
                        t={t}
                        tx={tx}
                        weeks={weeks}
                      />
                    ))}
                  </ol>

                  {/* Week 10 special preview entries (parent-only). */}
                  {isParent && week.id === 'week-10' && (
                    <div
                      className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2"
                      data-testid="path-week-10-parent-extras"
                    >
                      <Link
                        to={`${base}/week-10/quest`}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] text-[#1a1208] text-sm font-sans transition-colors"
                        data-testid="path-week-10-quest-link"
                      >
                        <Map className="w-4 h-4" />
                        {language === 'ru' ? 'Карта квеста' : 'Quest Map'}
                      </Link>
                      <Link
                        to={`${base}/nimny-night-run`}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] text-[#1a1208] text-sm font-sans transition-colors shadow-[0_0_24px_rgba(212,165,116,0.35)]"
                        data-testid="path-week-10-night-run-link"
                      >
                        <Gamepad2 className="w-4 h-4" />
                        {language === 'ru' ? 'Ночная игра Нимни' : 'Nimny Night Runner'}
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Closing message */}
      <Card className="mt-6 p-5 flex gap-3 items-start">
        <div className="w-10 h-10 rounded-full bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-200/85 shrink-0">
          <Star className="w-[18px] h-[18px]" />
        </div>
        <div>
          <div
            className="text-[#f3d9a4] text-base"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('goingStepByStep')}
          </div>
          <p className="mt-1 text-amber-100/75 font-serif text-sm leading-relaxed">
            {t('goingStepByStepDesc')}
          </p>
        </div>
      </Card>
      </div>
    </LabLayout>
  );
};

// Single day row. For children we keep the existing "open / locked" behaviour;
// for parent / super-admin previewing, we render an inline 3-button strip
// (Open lesson / Print / Parent guide) below the day title so they can review
// every lesson regardless of child progress.
const DayRow = ({ day, weekId, progress, base, isParent, language, t, tx, weeks }) => {
  const status = computeDayStatus(progress, weekId, day.id, weeks);
  const childLinkable = status !== 'locked';
  const lockedForChild = status === 'locked';

  const titleBlock = (
    <div className="flex items-start gap-3">
      <StepGlyph status={status} />
      <div className="flex-1 min-w-0">
        <div
          className={`font-sans ${
            status === 'locked' && !isParent
              ? 'text-amber-200/40'
              : 'text-[#f5ebe0]/90'
          }`}
        >
          <span style={{ fontFamily: 'var(--font-heading)' }}>
            {t('dayLabel')} {day.id.split('-')[1]}
          </span>{' '}
          · {tx(day.title)}
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <StatusPill status={status} t={t} />
          {isParent && lockedForChild && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-300/15 text-amber-200/60 text-[11px] font-sans"
              data-testid={`path-day-${weekId}-${day.id}-locked-for-child`}
            >
              <Lock className="w-3 h-3" />
              {language === 'ru' ? 'Закрыто для ребёнка' : 'Locked for child'}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <li
      className="-ml-[27px]"
      data-testid={`path-day-${weekId}-${day.id}`}
      data-status={status}
    >
      {/* Title row.
          - Child: clickable when not locked, plain div when locked.
          - Parent: never linked here — preview controls below handle it. */}
      {!isParent && childLinkable ? (
        <Link to={`${base}/${weekId}/${day.id}`} className="block py-1">
          {titleBlock}
        </Link>
      ) : (
        <div className="block py-1 select-none">{titleBlock}</div>
      )}

      {isParent && (
        <div
          className="ml-10 mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2"
          data-testid={`path-day-${weekId}-${day.id}-parent-actions`}
        >
          <Link
            to={`${base}/${weekId}/${day.id}`}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-amber-300/25 text-amber-200 hover:bg-amber-300/5 text-xs font-sans"
            data-testid={`path-day-${weekId}-${day.id}-parent-open-lesson`}
          >
            <BookOpen className="w-3.5 h-3.5" />
            {language === 'ru' ? 'Открыть урок' : 'Open lesson'}
          </Link>
          <Link
            to={`${base}/${weekId}/${day.id}/print`}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-amber-300/25 text-amber-200 hover:bg-amber-300/5 text-xs font-sans"
            data-testid={`path-day-${weekId}-${day.id}-parent-print`}
          >
            <Printer className="w-3.5 h-3.5" />
            {language === 'ru' ? 'Печать' : 'Print'}
          </Link>
          <Link
            to={`${base}/${weekId}/${day.id}?tab=parent`}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-amber-300/25 text-amber-200 hover:bg-amber-300/5 text-xs font-sans"
            data-testid={`path-day-${weekId}-${day.id}-parent-guide`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {language === 'ru' ? 'Гид родителя' : 'Parent guide'}
          </Link>
        </div>
      )}
    </li>
  );
};

const Legend = ({ kind, t }) => {
  const map = {
    open: { color: 'text-amber-300', label: t('statusOpen'), Icon: Star },
    in_progress: { color: 'text-amber-200', label: t('statusInProgress'), Icon: CircleDot },
    completed: { color: 'text-emerald-300', label: t('statusCompleted'), Icon: Star },
    locked: { color: 'text-amber-200/40', label: t('statusLocked'), Icon: Lock }
  };
  const m = map[kind];
  return (
    <div className="flex flex-col items-center gap-1">
      <m.Icon className={`w-4 h-4 ${m.color}`} />
      <span className="text-[10px] text-amber-200/65 font-sans">{m.label}</span>
    </div>
  );
};

const StepGlyph = ({ status }) => {
  const base =
    'w-7 h-7 rounded-full flex items-center justify-center border shrink-0 -translate-x-[11px]';
  if (status === 'completed')
    return (
      <span className={`${base} bg-emerald-400/15 border-emerald-300/35 text-emerald-200`}>
        <Star className="w-3.5 h-3.5" />
      </span>
    );
  if (status === 'in_progress' || status === 'open')
    return (
      <span
        className={`${base} bg-amber-300/15 border-amber-300/45 text-amber-200 shadow-[0_0_10px_rgba(212,165,116,0.45)]`}
      >
        <Star className="w-3.5 h-3.5" />
      </span>
    );
  return (
    <span className={`${base} bg-amber-300/5 border-amber-300/15 text-amber-200/40`}>
      <Lock className="w-3.5 h-3.5" />
    </span>
  );
};

export default MyPath;
